/**
 * LabReportsSection
 *
 * Inline card for the Patient Record page (right column).
 * Shows a thumbnail grid of uploaded lab reports with hover actions.
 */

import { useState, useCallback } from 'react';
import { Download, Eye, FolderOpen, Loader2, Trash2, Upload } from 'lucide-react';
import { Card, CardContent } from '@/components/base/Card';
import { useLabReports } from '@/lib/hooks/useLabReports';
import type { LabReportRecord } from '@/lib/api/labReports';
import {
  ACCEPTED_EXT,
  FileIcon,
  useLabReportActions,
  ReportPreviewDialog,
  DeleteConfirmDialog,
  StagedFilesList,
} from './lab-reports-shared';

// --- Props ---

interface LabReportsSectionProps {
  patientId?: string;
}

// --- Component ---

export function LabReportsSection({ patientId }: LabReportsSectionProps) {
  const { data: reports = [], isLoading: isFetching } = useLabReports(patientId);
  const {
    inputRef,
    isUploading,
    isDeleting,
    stagedFiles,
    removeStagedFile,
    clearStagedFiles,
    uploadStagedFiles,
    previewStagedFile,
    handleDelete,
    loadReportUrl,
    onInputChange,
  } = useLabReportActions({
    patientId,
  });

  const [previewReport, setPreviewReport] = useState<LabReportRecord | null>(null);
  const [loadingReportId, setLoadingReportId] = useState<string | null>(null);

  /** Load binary lazily, then view */
  const handleViewReport = useCallback(
    async (report: LabReportRecord) => {
      const cachedUrl = report.url;
      if (cachedUrl) {
        if (report.contentType === 'application/pdf') {
          window.open(cachedUrl, '_blank', 'noopener');
        } else {
          setPreviewReport({ ...report, url: cachedUrl });
        }
        return;
      }
      setLoadingReportId(report.id);
      const url = await loadReportUrl(report);
      setLoadingReportId(null);
      if (!url) return;
      if (report.contentType === 'application/pdf') {
        window.open(url, '_blank', 'noopener');
      } else {
        setPreviewReport({ ...report, url });
      }
    },
    [loadReportUrl]
  );
  const [deleteConfirm, setDeleteConfirm] = useState<LabReportRecord | null>(null);

  return (
    <>
      <Card
        padding="none"
        className="mb-4 rounded-[20px] border-[#fbfafd] bg-[rgba(253,252,250,0.5)] backdrop-blur-[15px]"
      >
        <CardContent className="flex flex-col gap-4 p-5">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-[#061A61]" />
              <h2 className="font-hanken text-[20px] font-medium leading-6 tracking-[0px] text-[#42434B]">
                Lab Reports
              </h2>
            </div>

            <button
              type="button"
              disabled={isUploading || !patientId}
              onClick={() => inputRef.current?.click()}
              className="flex items-center gap-1.5 rounded-lg border border-[#061A61] px-3 py-1.5 font-hanken text-sm font-medium text-[#061A61] transition-colors hover:bg-[#061A61]/5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Upload
            </button>

            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED_EXT}
              multiple
              className="hidden"
              onChange={onInputChange}
            />
          </div>

          {/* Thumbnail Grid */}
          {isFetching ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-[#4B6EE7]" />
            </div>
          ) : reports.length === 0 ? (
            <p className="text-center font-hanken text-sm text-[#8D90A1]">
              No reports uploaded yet
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="group relative flex h-[80px] w-[60px] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-bl-[5px] rounded-br-[5px] rounded-tl-[5px] rounded-tr-[20px] border border-[#EAEBF3] bg-white shadow-[1px_1px_4px_1px_rgba(151,151,151,0.2)]"
                  title={report.fileName}
                  onClick={() => handleViewReport(report)}
                >
                  {report.contentType.startsWith('image/') && report.url ? (
                    <img
                      src={report.url}
                      alt={report.fileName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#EEF2FF]">
                        <FileIcon contentType={report.contentType} />
                      </div>
                      <span className="px-1 text-center font-hanken text-[9px] font-semibold uppercase tracking-wide text-[#4B6EE7]">
                        {report.fileType}
                      </span>
                    </div>
                  )}

                  {/* Hover overlay */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 rounded-[inherit] bg-[#061A61]/70 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      type="button"
                      title="View"
                      disabled={loadingReportId === report.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewReport(report);
                      }}
                      className="flex h-6 w-6 items-center justify-center rounded-md bg-white/20 text-white hover:bg-white/40"
                    >
                      {loadingReportId === report.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Eye className="h-3.5 w-3.5" />
                      )}
                    </button>
                    {report.url && (
                      <a
                        href={report.url}
                        download={report.fileName}
                        title="Download"
                        onClick={(e) => e.stopPropagation()}
                        className="flex h-6 w-6 items-center justify-center rounded-md bg-white/20 text-white hover:bg-white/40"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </a>
                    )}
                    <button
                      type="button"
                      title="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm(report);
                      }}
                      className="flex h-6 w-6 items-center justify-center rounded-md bg-red-500/70 text-white hover:bg-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Staged Files */}
      {stagedFiles.length > 0 && (
        <Card
          padding="none"
          className="mb-4 rounded-[20px] border-[#fbfafd] bg-[rgba(253,252,250,0.5)] backdrop-blur-[15px]"
        >
          <StagedFilesList
            stagedFiles={stagedFiles}
            isUploading={isUploading}
            onPreview={previewStagedFile}
            onRemove={removeStagedFile}
            onCancel={clearStagedFiles}
            onUploadAll={uploadStagedFiles}
          />
        </Card>
      )}

      <ReportPreviewDialog report={previewReport} onClose={() => setPreviewReport(null)} />

      <DeleteConfirmDialog
        report={deleteConfirm}
        isDeleting={isDeleting}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => handleDelete(deleteConfirm, () => setDeleteConfirm(null))}
      />
    </>
  );
}
